import * as React from "react";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {IBulkConfig, IBulkFormData, Source} from "../../../../../../config/bulk";
import {bulkUpload, getBulkConfig} from "../../../../../../stores/bulk";
import {FileField} from "./FileField";
import {useGlobalStores} from "../../../../../../hooks/use_global_stores";
import {SelectField} from "../../../../../Components/Form/SelectField";
import {Form} from "../../../../../Components/Form/Form/Form";
import { OutlinedContainer } from "../../../../../Components/Form/OutlinedContainer";
import { useForm } from "../../../../../../hooks/use_form_v2";
import { Expenses24FileUploadField } from "./Expenses24FileUploadField";

const LABELS = {
    [Source.DE_AMAZON_VISA]: 'Amazon Visa (Legacy)',
    [Source.DE_AMAZON_VISA_ZINIA]: 'Amazon Visa (Zinia)',
    [Source.DE_AMEX]: 'American Express',
    [Source.DE_DKB]: 'DKB',
    [Source.DE_N26]: 'N26',
    [Source.GLOBAL_EXPENSES_24]: 'Expenses24 (manual mapping)',
};

interface ISelectFormStepProps {
    onSubmit(promise: Promise<any>): void;
}

interface IBulkImportState {
    region: string;
    source: string;
    format: string;
    file: File | null;
}

const DEFAULT_REGION = 'de';

const getExample = (config: IBulkConfig | null | undefined, region: string, source: string): string | undefined => {
    return config?.regions.find(r => r.region === region)?.sources.find(s => s.source === source)?.example;
};

const getFormats = (config: IBulkConfig | null | undefined, region: string, source: string): string[] => {
    return config?.regions.find(r => r.region === region)?.sources.find(s => s.source === source)?.available_formats || [];
};

export const SelectFormStep = ({onSubmit}: ISelectFormStepProps) => {
    const { t } = useTranslation();

    const { snackbarStore } = useGlobalStores();

    const form = useForm<IBulkImportState>({
        region: DEFAULT_REGION,
        source: '',
        format: '',
        file: null,
    });
    const { setLoading, register, change, watch, isLoading } = form;

    const [configLoading, setConfigLoading] = useState(false);
    const [config, setConfig] = useState<IBulkConfig | null>();

    const handleCsvMappingChange = (data?: IBulkFormData[] | null) => {
        if (!data) {
            return;
        }

        onSubmit(Promise.resolve(data));
    };

    const handleSubmit = (values: IBulkImportState): Promise<any> => {
        if (!values.file) {
            return Promise.reject();
        }

        setLoading(true);
        snackbarStore.close();

        const formData = new FormData();
        formData.append("format", values.format);
        formData.append("source", values.source);
        formData.append("file", values.file!!, values.file!!.name);

        return bulkUpload(formData)
            .then(result => {
                setLoading(false);

                onSubmit(Promise.resolve(result));

                return Promise.resolve();
            })
            .catch(reason => {
                reason.then((error: any) => {
                    setLoading(false);

                    snackbarStore.open(false);

                    return Promise.reject();
                });
            });
    };

    useEffect(() => {
        (async () => {
            setConfigLoading(true);

            const result = await getBulkConfig();
            setConfig(result);

            setConfigLoading(false);
        })();
    }, []);

    useEffect(() => {
        const formats = getFormats(config, watch('region'), watch('source'));
        if (formats.length === 0) {
            return;
        }
        change('format')(formats[0]);
    }, [config, watch('region'), watch('source')]);

    if (configLoading || !config) {
        return (
            <CircularProgress size={40} />
        );
    }

    return (
        <Form
            form={form}
            onSubmit={handleSubmit}
            // errors={['foo' as any]}
            submitButtonProps={{
                label: t('transactions.bulk.upload_form.next') || '',
                disabled: (
                    isLoading() ||
                    (watch('source') === Source.GLOBAL_EXPENSES_24) ||
                    !watch('file')
                ),
            }}
        >
            <Form.Field
                {...register('source')}
                conditional={watch('region') !== ''}
                type={SelectField}
                options={config!!.regions.find(r => r.region === watch('region'))?.sources.map(s => s.source) || []}
                optionValueGetter={(source: any) => source}
                optionLabelGetter={(source: any) => (LABELS as any)[source] || source}
            />

            <Form.ContainerField conditional={!!getExample(config, watch('region'), watch('source'))}>
                <OutlinedContainer label="Example">
                    <pre style={{ margin: 0, padding: 0, fontSize: 12 }}>
                        {getExample(config, watch('region'), watch('source'))}
                    </pre>
                </OutlinedContainer>
            </Form.ContainerField>

            <Form.Field
                {...register('file')}
                conditional={watch('source') !== '' && watch('source') !== Source.GLOBAL_EXPENSES_24}
                type={FileField}
                formats={getFormats(config, watch('region'), watch('source'))}
            />
            <Form.Field
                type={Expenses24FileUploadField}
                conditional={watch('source') === Source.GLOBAL_EXPENSES_24}
                onChange={handleCsvMappingChange}
                formats={getFormats(config, watch('region'), watch('source'))}
            />
        </Form>
    );
};
